import { useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import dayjs from "dayjs";
import { useQuery } from "@apollo/client";
import { Button, ExpandableText, Icon, Rating } from "@aqar-app/react-ui";
import config from "@/common/config";
import ProfileCardSkeleton from "../ProfileCardSkeleton";
import { getElapsedTime } from "@/lib/dateHelpers";
import { GET_USER_USAGE } from "@/graphql/queries/user";
import { useAuthState } from "@/contexts/auth/auth.provider";
import UserInfo from "../constants";
import styles from "./.module.scss";
import ProfileAdSpaces from "@/components/UserScreen/AuthProfileCard/ProfileAdSpaces";
import AdSpacesV2 from "@/components/Listing/ListingScreen/AdSpaceV2";
import { useAppState } from "@/contexts/app/app.provider";

const AuthProfileCard = ({ loading, user = {} }) => {
  const { token } = useAuthState();
  const { data: packageData } = useQuery(GET_USER_USAGE, {
    skip: !user?.paid || !token,
    context: { token },
  });
  const { isMobile } = useAppState();

  const { expire_time } = packageData?.Offices?.Package?.usage || {};

  const userTags = useMemo(() => {
    const tags = UserInfo.filter((info) => user[info.key] === info.value) || [];

    if (!user?.last_seen) return tags;
    return [
      {
        label: getElapsedTime(user?.last_seen),
        icon: "Alarm",
      },
      ...tags,
    ];
  }, [user?.last_seen, UserInfo]);

  if (loading) {
    return <ProfileCardSkeleton />;
  }

  return (
    <>
      {/*<ProfileAdSpaces id={976477} />*/}
      <AdSpacesV2
        adTypes={[isMobile ? "user_profile_small" : "user_profile_wide"]}
      />
      <div className={styles.profileCard}>
        <span>
          <Link href="/user-actions/profile-update">
            <Button className={styles.settingBtn} outline>
              <p>إعدادات الحساب</p>
              <Icon
                alt="إعدادات الحساب"
                name="setting"
                width={20}
                height={20}
              />
            </Button>
          </Link>
        </span>
        <div className={styles.content}>
          <div className={styles.ownerImg}>
            <Image
              unoptimized
              src={`${config.IMAGES_CDN_BASE_URL}users/${user?.img || null}`}
              alt="Profile Image"
              width={100}
              height={100}
            />
          </div>
          <div className={styles.userName}>
            <span>{user.name}</span>
            {user.status === 1 && (
              <svg
                className={styles.banned}
                width={20}
                height={20}
                focusable="false"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                  fill="#A51B1B"
                />
              </svg>
            )}
          </div>
          {user.review ? (
            <div className={styles.userReview}>
              <Rating value={(user?.review / 5) * 100} count={5} />
              <span className={styles.count}>{`(${user.reviewsCount})`}</span>
            </div>
          ) : (
            <div style={{ color: "#666666" }}>لا يوجد تقييم بعد</div>
          )}
          {user.create_time ? (
            <div className={styles.date}>{`الإنضمام: ${dayjs(
              user.create_time * 1000,
            ).format("ll")}`}</div>
          ) : null}
          {expire_time ? (
            <div className={styles.date}>
              {`انتهاء الاشتراك: ${dayjs(expire_time).format("ll")}`}
            </div>
          ) : null}
          <div className={styles.description}>
            <ExpandableText buttonText=" اقرأ المزيد">
              {user.about}
            </ExpandableText>
          </div>
          <ul className={styles.userInfo}>
            {userTags.map((info) => {
              return (
                <li className={styles.info} key={info.label}>
                  <Icon
                    name={info.icon}
                    className={styles.infoIcon}
                    width={25}
                    height={25}
                  />
                  <span className={styles.InfoLabel}>{info.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
};

export default AuthProfileCard;
